import { siteSettings } from "../static";

/**
 * Rewrite legacy Cloudinary / Firebase Storage URLs to Bunny at display time.
 * DB values stay unchanged. New uploads already return Bunny CDN URLs.
 *
 * Cloudinary:
 *   https://res.cloudinary.com/<cloud>/image/upload[/transforms]/v123/path/file.ext
 *   → https://<mediaBaseUrl>/path/file.ext
 *
 * Firebase Storage URLs are left as-is unless
 * siteSettings.rewriteFirebaseStorage is true.
 */

const CLOUDINARY_HOST = "res.cloudinary.com";

function isTransformOrVersion(segment) {
  if (!segment) return false;
  if (/^v\d+$/.test(segment)) return true;
  if (!segment.includes(".") && /[_|,]/.test(segment)) return true;
  return false;
}

function cloudNames() {
  const names = new Set();
  if (siteSettings.cloudName) names.add(siteSettings.cloudName);
  for (const n of siteSettings.cloudinaryCloudNames || []) names.add(n);
  return names;
}

export function cloudinaryPathFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.hostname !== CLOUDINARY_HOST) return null;

  const parts = parsed.pathname.split("/").filter(Boolean);
  // /<cloud>/<resource_type>/<delivery_type>/...
  if (parts.length < 4) return null;
  const [cloudName, , deliveryType, ...rest] = parts;
  if (!cloudNames().has(cloudName)) return null;
  if (!["upload", "private", "authenticated"].includes(deliveryType)) {
    return null;
  }

  let i = 0;
  while (i < rest.length && isTransformOrVersion(rest[i])) i++;
  const mediaPath = rest.slice(i).join("/");
  return mediaPath || null;
}

/** Best-effort path from a Firebase Storage download URL. */
export function firebaseStoragePathFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  if (
    !url.includes("firebasestorage.googleapis.com") &&
    !url.includes("firebasestorage.app")
  ) {
    return null;
  }
  try {
    const parsed = new URL(url);
    const m = parsed.pathname.match(/\/o\/(.+)$/);
    if (!m) return null;
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") return url;
  const base = (siteSettings.mediaBaseUrl || "").replace(/\/$/, "");
  if (!base) return url;

  if (url.startsWith(base + "/") || url === base) return url;

  const cloudPath = cloudinaryPathFromUrl(url);
  if (cloudPath) return `${base}/${cloudPath}`;

  if (siteSettings.rewriteFirebaseStorage) {
    const fbPath = firebaseStoragePathFromUrl(url);
    if (fbPath) return `${base}/${fbPath}`;
  }

  return url;
}

/**
 * Patch HTMLImageElement so every <img src=...> (including React) resolves media.
 * Safe to call once at app boot.
 */
export function installMediaResolver() {
  if (typeof window === "undefined") return;
  if (window.__bunnyMediaResolverInstalled) return;
  window.__bunnyMediaResolverInstalled = true;

  const proto = HTMLImageElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, "src");
  if (desc && desc.set && desc.get) {
    Object.defineProperty(proto, "src", {
      configurable: true,
      enumerable: desc.enumerable,
      get() {
        return desc.get.call(this);
      },
      set(value) {
        desc.set.call(this, resolveMediaUrl(value));
      },
    });
  }

  const originalSetAttribute = proto.setAttribute;
  proto.setAttribute = function setAttribute(name, value) {
    if (String(name).toLowerCase() === "src") {
      return originalSetAttribute.call(this, name, resolveMediaUrl(value));
    }
    return originalSetAttribute.call(this, name, value);
  };
}
